import { fetchAuthSession } from 'aws-amplify/auth';
import axios, { AxiosInstance } from 'axios';

import { simpleIsoDateTransformer } from './utils';

const { VITE_API_URL } = import.meta.env;

export const useAxiosClient = async (): Promise<AxiosInstance> => {
  const session = await fetchAuthSession();
  const accessToken = session.tokens?.accessToken?.toString() ?? '';
  const idToken = session.tokens?.idToken?.toString();

  const headers: Record<string, string> = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
  };
  if (idToken) {
    headers['X-Id-Token'] = idToken;
  }

  const client = axios.create({
    baseURL: VITE_API_URL,
    headers,
    transformResponse: [
      // Transforms any date strings into Date objects. Does not work for all possible date strings, only those made with new Date().toISOString()
      (data) => {
        // Skip processing if no data is present
        if (!data) {
          return data;
        }
        // Parse the JSON string into an object
        const parsed = JSON.parse(data);

        // Recursive function to transform date strings into Date objects
        const transformDates = (item: unknown): unknown => {
          // Return early only for null/undefined and non-string primitives
          if (
            item === null ||
            (typeof item !== 'object' && typeof item !== 'string')
          ) {
            return item;
          }

          // Handle strings directly
          if (typeof item === 'string' && simpleIsoDateTransformer(item)) {
            return new Date(item);
          }

          // Special handling for arrays - map through each element
          if (Array.isArray(item)) {
            return item.map((arrayItem) => transformDates(arrayItem));
          }

          // Process each key-value pair in the object
          if (typeof item === 'object') {
            for (const [key, value] of Object.entries(
              item as Record<string | number | symbol, unknown>,
            )) {
              if (
                typeof value === 'string' &&
                simpleIsoDateTransformer(value)
              ) {
                (item as Record<string | number | symbol, unknown>)[key] =
                  new Date(value);
              } else if (value && typeof value === 'object') {
                (item as Record<string | number | symbol, unknown>)[key] =
                  transformDates(value);
              }
            }
          }
          return item;
        };

        // Transform the entire parsed object and return it
        return transformDates(parsed);
      },
    ],
  });
  return client;
};
